import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { usePagination } from "@components/index";
import {
  getStatusClassName,
  filterDataBySearch,
  generateGlobalSearchOptions,
} from "@utils/dataUtils";
import {
  formatDate,
  formatDateToYYYYMMDD,
  isValidDateRange,
} from "@utils/dateUtils";
import { fetchClaimsList } from "@actions/fetchClaimsAction";
import { useCSVExport } from "@hooks/useCSVExport";
import { MIS_FIELDS_CLAIMS } from "@constants/misDownloadData";
import { useAppConfig } from "@src/context/AppConfigContext";

export const CLAIM_STATUS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

/**
 * Claims-specific utility functions
 */
export const getStatusBadgeClass = (status) => {
  return getStatusClassName(status, "claims-table__status");
};

export const formatVoucherExpiry = (expiryDate) => {
  if (!expiryDate) return "N/A";
  return new Date(expiryDate).toLocaleDateString("en-IN");
};

export const searchOptions = generateGlobalSearchOptions(
  "Search across all claims data (PNR, claim ID, Status, etc.)",
);

// Define searchable fields for claims
const CLAIMS_SEARCH_FIELDS = [
  "claim_id",
  "claim_status",
  "customer_email",
  "customer_mobile",
  "policy_reference_no",
];

/**
 * API service functions
 */
export const fetchClaimsData = async (urlParams) => {
  try {
    const response = await fetchClaimsList(urlParams);

    if (response.success) {
      return {
        success: true,
        data: response.data.claims,
        error: null,
      };
    } else {
      return {
        success: false,
        data: [],
        error: response.message,
      };
    }
  } catch (err) {
    return {
      success: false,
      data: [],
      error: "Failed to fetch claims",
    };
  }
};

const mapClaimsToMISData = (claim, index) => ({
  "SR. No": index + 1,
  "Claim ID": claim.claim_id || "-",
  "PNR ": claim.pnr_number || "-",
  "Train No": claim.train_number || "-",
  "Travel Date": formatDate(claim.journey_date) || "-",
  "Ticket Fare (₹)": claim.fare_amount || "-",
  "Waitlist After Chart (Y/N)": claim.final_ticket_status ? "Y" : "-",
  "Claim Received Date": claim.claim_received_date
    ? formatDate(claim.claim_received_date)
    : "-",
  "Claim Approved (Y/N)": claim.claim_status,
  "Claim Amount (₹)": claim.claim_amount || "-",
  "Settlement Date": claim.settlement_datetime
    ? formatDate(claim.settlement_datetime)
    : "-",
  "Mode of Payment": claim.payment_mode || "-",
});

/**
 * Filter function for claims
 */
export const filterClaims = (claims, searchValue) => {
  return filterDataBySearch(claims, searchValue, CLAIMS_SEARCH_FIELDS);
};

/**
 * Custom hook for Claims management
 */
export const useClaimsData = () => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("");
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({});

  const isInitialMount = useRef(true);
  const { selectedClient } = useAppConfig();

  // XLSX export hook
  const { exportToXLSX, generateDateFilename } = useCSVExport();

  // Load claims data
  const loadClaimsData = useCallback(
    async (urlParams) => {
      setLoading(true);

      const params = `${urlParams}&clientname=${selectedClient?.clientName}`;

      const result = await fetchClaimsData(params);
      setClaims(result.data || []);
      setError(result.error);
      setLoading(false);
    },
    [selectedClient?.clientName],
  );

  // Initial data load
  useEffect(() => {
    if (isInitialMount.current) {
      const urlParams =
        "startDate=" +
        formatDateToYYYYMMDD(new Date()) +
        "&endDate=" +
        formatDateToYYYYMMDD(new Date());

      isInitialMount.current = false;
      loadClaimsData(urlParams);
    } else {
      if (selectedStatusFilter || isValidDateRange(fromDate, toDate, false)) {
        let urlParams = "";

        if (selectedStatusFilter) {
          urlParams = "status=" + selectedStatusFilter + "&";
        }

        if (isValidDateRange(fromDate, toDate, false)) {
          urlParams =
            urlParams +
            "startDate=" +
            formatDateToYYYYMMDD(fromDate) +
            "&endDate=" +
            formatDateToYYYYMMDD(toDate);
        }

        loadClaimsData(urlParams);
      }
    }
  }, [fromDate, loadClaimsData, selectedStatusFilter, toDate]);

  // Filter claims based on search only (status and date filters are handled server-side)
  const filteredClaims = useMemo(() => {
    let filtered = filterClaims(claims, searchValue);

    return filtered;
  }, [claims, searchValue]);

  // Handle search
  const handleSearch = useCallback((value) => {
    setSearchValue(value);
  }, []);

  // Handle date range change
  const handleDateChange = useCallback((from, to) => {
    setFromDate(from);
    setToDate(to);
  }, []);

  // Handle status filter change
  const handleStatusFilterChange = useCallback((status) => {
    setSelectedStatusFilter(status);
  }, []);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    setFromDate(new Date());
    setToDate(new Date());
    setSelectedStatusFilter("");
    setSearchValue("");
  }, []);

  // Modal handlers
  const handleViewClaim = useCallback((claim) => {
    setSelectedClaim(claim);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedClaim(null);
  }, []);

  // Handle action click (approve/reject) - opens confirmation modal
  const handleActionClick = useCallback((claim, action) => {
    setModalConfig({ claimData: claim, action });
  }, []);

  // Handle cancellation of action
  const handleCancelAction = useCallback(() => {
    setShowConfirmModal(false);
    setModalConfig({});
  }, []);

  // Handle MIS data download using the XLSX export hook
  const handleDownloadMISClaims = useCallback(() => {
    const filename = generateDateFilename("MIS_Data_Claims");
    exportToXLSX(
      filteredClaims,
      MIS_FIELDS_CLAIMS,
      filename,
      mapClaimsToMISData,
      "Claims",
    );
  }, [exportToXLSX, generateDateFilename, filteredClaims]);

  // Pagination functionality
  const {
    currentPage,
    currentData: paginatedClaims,
    handlePageChange,
    totalPages,
  } = usePagination(filteredClaims, 10);

  return {
    // Data
    claims: paginatedClaims, // Paginated claims for display
    allClaims: filteredClaims, // All filtered claims for pagination component
    loading,
    error,

    // Search & Filters
    searchValue,
    handleSearch,
    fromDate,
    toDate,
    handleDateChange,
    selectedStatusFilter,
    handleStatusFilterChange,
    handleClearFilters,

    // Modal Management
    selectedClaim,
    isModalOpen,
    showConfirmModal,
    modalConfig,
    handleViewClaim,
    handleCloseModal,
    handleActionClick,
    handleCancelAction,
    loadClaimsData,

    // mis data button
    handleDownloadMISClaims,
    // Pagination
    currentPage,
    handlePageChange,
    totalPages,
  };
};
